import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import * as actions from '../../store/actions';
import { withRouter } from 'react-router-dom';

import {
  Container,
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  NavLinkStyled,
  LogoText,
  Appbar,
} from './styles';
import CheckCircle from "@mui/icons-material/CheckCircle";
import CloseIcon from '@mui/icons-material/Close';

import { isEmpty } from 'lodash';
import Spinner from '../../components/Spinner/index';

import { roleNames } from '../../utils/roleNames';
import { i18n } from '../../translate/i18n';
import { RedirectModal } from './RedirectModal';
import { toastr } from 'react-redux-toastr';

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative'
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "green",
    '&:hover': {
      backgroundColor: "green",
      opacity: .90,
    },
  },
}));

const ConfirmCreateUser = (props: any) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [role, setRole] = useState('doorman');


  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [tenant, setTenant] = useState<any>({});
  const [email, setEmail] = useState("");

  const [alreadyUsed, setAlreadyUsed] = useState(false);

  const [passwordError, setPasswordError] = useState(false);
  const [passwordConfirmationError, setPasswordConfirmationError] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [nameFullError, setNameFullError] = useState(false);

  const [redirectModalOpen, setRedirectModalOpen] = useState(false);
  const [redirectModalType, setRedirectModalType] = useState(null);

  const getTokenConfirmAddTenantOrUser: any = useCallback(
    () => dispatch(actions.getTokenConfirmAddTenantOrUser(props.match.params.id)), []
  );

  const userToken = useSelector((state: any) => state.tokens.userToken);

  const inviteCreateUser: any = (body: any) => dispatch(actions.inviteCreateUser(body))

  const loadingUser = useSelector((state: any) => state.users.loading);
  const loadingToken = useSelector((state: any) => state.tokens.loading);



  useEffect(() => {
    getTokenConfirmAddTenantOrUser().then((response: any) => {
      if (response === 'error') {
        setAlreadyUsed(true);
      }
    })
  }, [])

  // in case editing, populate fields
  useEffect(() => {
    if (!isEmpty(userToken)) {
      setTenant(userToken.tenantDetails.tenant)
      setRole(userToken.tenantDetails.role)
      setEmail(userToken.userDetails.email)
    }
  }, [userToken])

  const handleSubmit = (e: any) => {
    e.preventDefault();
    let hasError = false;

    if (!name) {
      setNameError(true);
      hasError = true;
    }

    if (name.split(" ").length < 2) {
      setNameFullError(true);
      hasError = true;
    }

    if (!validPassword()) {
      hasError = true;
    }

    if (passwordConfirmation !== password) {
        setPasswordConfirmationError(true)
        hasError = true;
    }

    if (hasError) return;

    let body = {
      name,
      password,
      tokenId: props.match.params.id
    }

    inviteCreateUser(body).then((response: any) => {
      if (response === 'success') {
        setUserCreated(true);
      } else {        
        setRedirectModalType(response?.code || null);
        setRedirectModalOpen(true);        
      }
    })

  }

  const validPassword = () => {
    if (password.length < 6) {
      setPasswordError(true)
      return false;
    }
    return true;
  }

  const validPasswordConfirmation = () => {
    if (password === passwordConfirmation) {
      return true;
    } else {
      setPasswordConfirmationError(true)
      return false;
    }
  }

  const onChangePassword = (event: any) => {
    if (passwordError && event.target.value.length >= 6) {
      setPasswordError(false);
    }
    setPasswordConfirmation("")
    setPasswordConfirmationError(false);
    return setPassword(event.target.value)
  }

  const onChangePasswordConfirmation = (event: any) => {
    if (password === event.target.value) {
      setPasswordConfirmationError(false);
    }
    return setPasswordConfirmation(event.target.value)
  }

  const card = () => (
    <Box sx={styles.cardContainer}>
      {tenant.photo ? <Avatar src={tenant.photo.url} sx={styles.avatar} /> : <Avatar sx={styles.avatarSmallText}>LOGO</Avatar>}
      <Box sx={styles.cardBody}>
        <Box sx={styles.nameText}>{tenant.name}</Box>
        <Box sx={styles.roleText}>{roleNames(role)}</Box>
      </Box>
    </Box>
  )

  return (
    <>
      <Appbar />
      <Container component="main" maxWidth="xs" sx={{ marginBottom: '100px' }}>
        <CssBaseline />
        {(!userCreated && !alreadyUsed) && (
          <Box className={classes.paper}>
            <LogoText />
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              {loadingToken && <Spinner overlay />}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    autoComplete="name"
                    name="Name"
                    variant="outlined"
                    required
                    fullWidth
                    id="name"
                    label={i18n.t('textFieldConfirmCreateUser.name') as string}
                    autoFocus
                    helperText={nameFullError && i18n.t('textFieldConfirmCreateUser.helperText') as string}
                    value={name}
                    error={nameError || nameFullError}
                    onChange={event => { setNameError(false); setNameFullError(false); return setName(event.target.value) }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={i18n.t('textFieldConfirmCreateUser.email') as string}
                    name="email"
                    autoComplete="email"
                    value={email}
                    disabled={true}
                    onChange={(event: any) => setEmail(event)}
                  />
                </Grid>
                {card()}

                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label={i18n.t('textFieldConfirmCreateUser.password') as string}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    helperText={i18n.t('textFieldConfirmCreateUser.helperTextPassword') as string}
                    error={passwordError}
                    onChange={event => onChangePassword(event)}
                    onBlur={validPassword}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="confirmPassword"
                    label={i18n.t('textFieldConfirmCreateUser.confirmPassword') as string}
                    type="password"
                    id="confirmPassword"
                    autoComplete="confirm-password"
                    value={passwordConfirmation}
                    error={passwordConfirmationError}
                    onChange={event => onChangePasswordConfirmation(event)}
                    helperText={passwordConfirmationError && i18n.t('textFieldConfirmCreateUser.helperTextConfirmPassword') as string}
                    onBlur={validPasswordConfirmation}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={loadingUser}
                startIcon={loadingUser && <CircularProgress />}
              >
                {i18n.t('btnConfirmCreateUser.text') as string}
          </Button>
            </form>
          </Box>)}
        {userCreated && (
          <Box className={classes.paper}>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
              <CheckCircle sx={{ height: "100px", width: "100px", color: "green" }} />
              <Box sx={{ fontSize: "25px", fontWeight: 'bold', textAlign: "center" }}>{i18n.t('divConfirmCreateUser.text') as string}</Box>
              <NavLinkStyled variant="body2" to='/login'>{i18n.t('navLinkConfirmCreateUser.text')}</NavLinkStyled>
            </Box>
          </Box>
        )}
        {alreadyUsed && (
          <Box className={classes.paper}>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: "100%", height: "100%" }}>
              <CloseIcon sx={{ height: "100px", width: "100px", color: "red" }} />
              <Box sx={{ fontSize: "25px", fontWeight: 'bold', textAlign: "center" }}>{i18n.t('divConfirmCreateUser.link') as string}</Box>
              <NavLinkStyled variant="body2" to='/login'>{i18n.t('navLinkConfirmCreateUser.text')}</NavLinkStyled>
            </Box>
          </Box>
        )}
        <RedirectModal 
          isModalOpen={redirectModalOpen} 
          closeDialog={() => setRedirectModalOpen(false)} 
          type={redirectModalType} 
        />
      </Container>
    </>
  );
}

export default withRouter(ConfirmCreateUser)

const styles = {
  cardContainer: {
    display: 'flex',
    flexDirection: 'row',
    border: "1px solid #ccc",
    padding: "10px 20px",
    borderRadius: "10px",
    width: "80%",
    maxWidth: "400px",
    marginTop: "15px",
    marginBottom: "10px",
    marginLeft: "15px",
    position: "relative",
  },
  avatar: {
    width: 50,
    height: 50,
  },
  avatarSmallText: {
    fontSize: 12,
    width: 50,
    height: 50,
  },
  cardBody: {
    display: "flex",
    justifyContent: "center",
    paddingLeft: 10,
    flexDirection: 'column'
  },
  roleText: {
    fontSize: 12
  },
  nameText: {
    fontWeight: "bold"
  },
}
